import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

// UNIFIED LOGIN - One login for everyone
export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Check for client logins first (Arena)
    const clientCredentials = {
      'vikas@esportsxo.gg': { password: '9873060792', tenant: 'arena', redirect: '/client/dashboard' },
    };

    const clientMatch = clientCredentials[email.toLowerCase()];
    
    if (clientMatch && password === clientMatch.password) {
      localStorage.setItem('client_authenticated', 'true');
      localStorage.setItem('client_tenant', clientMatch.tenant);
      if (rememberMe) {
        localStorage.setItem('remember_email', email);
      }
      navigate(clientMatch.redirect);
      setLoading(false);
      return;
    }

    // Try XO admin login
    const result = await login(email, password);
    
    if (result.success) {
      if (rememberMe) {
        localStorage.setItem('remember_email', email);
      }
      navigate('/dashboard');
    } else {
      setError(result.error || 'Invalid email or password');
    }
    setLoading(false);
  };

  // Load remembered email on mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('remember_email');
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  return (
    <div className="min-h-screen flex" style={{ background: '#0F172A' }}>
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-lg overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_tournament-finder-2/artifacts/bgo2ljk1_XO%20Logo%282200x2200%29.png"
                alt="XOClaw"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <Link to="/" className="font-bold text-xl text-white">XOClaw</Link>
              <p className="text-sm text-[#94A3B8]">AI Agent Platform</p>
            </div>
          </div>

          <h1 className="font-bold text-2xl sm:text-3xl text-white mb-2">Welcome back</h1>
          <p className="text-[#94A3B8] mb-8">Sign in to access your dashboard</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-3 rounded-lg text-sm" style={{ background: '#F5920B20', border: '1px solid #F5920B40', color: '#F59E0B' }}>
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-white">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                className="bg-[#1E293B] border-[#475569] text-white placeholder:text-[#64748B]"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white">Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="bg-[#1E293B] border-[#475569] text-white placeholder:text-[#64748B] pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="remember"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-[#475569] bg-[#1E293B] text-[#00D4AA] focus:ring-[#00D4AA]"
              />
              <label htmlFor="remember" className="text-sm text-[#94A3B8]">Remember me</label>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#00D4AA] text-black hover:bg-[#00E6BB] font-bold"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Sign In
            </Button>
          </form>
        </div>
      </div>

      <div 
        className="hidden lg:flex lg:w-1/2 items-center justify-center p-12"
        style={{ background: 'linear-gradient(135deg, #00D4AA15 0%, #0F172A 100%)' }}
      >
        <div className="text-center">
          <div className="w-40 h-40 rounded-2xl overflow-hidden mx-auto mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_tournament-finder-2/artifacts/bgo2ljk1_XO%20Logo%282200x2200%29.png"
              alt="XOClaw"
              className="w-full h-full object-cover"
            />
          </div>
          <h2 className="font-bold text-3xl text-white mb-2">XOClaw</h2>
          <p className="text-[#94A3B8] mb-6">AI Agent Platform for Esports</p>
          <div className="flex justify-center gap-2">
            <span className="px-3 py-1 rounded-full bg-[#00D4AA]/10 text-[#00D4AA] text-xs">Matchmaking</span>
            <span className="px-3 py-1 rounded-full bg-[#6366F1]/10 text-[#6366F1] text-xs">Support</span>
            <span className="px-3 py-1 rounded-full bg-[#F59E0B]/10 text-[#F59E0B] text-xs">Sales</span>
          </div>
        </div>
      </div>
    </div>
  );
}
